import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Register from "../../assets/register.jpg";
import PillButton from "components/button/Pills";
import { useNavigate } from "react-router-dom";

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm Password is required"),
  phoneNumber: Yup.string()
    .matches(/^\d+$/, "Phone number must contain only digits")
    .required("Phone number is required"),
  companyName: Yup.string().required("Company Name is required"),
  website: Yup.string()
    .url("Invalid URL format")
    .required("Website is required"),
  numEmployees: Yup.number()
    .positive("Number of employees must be positive")
    .required("Number of employees is required"),
  // address1: Yup.string().required("Address Line 1 is required"),
  // address2: Yup.string().required("Address Line 2 is required"),
  // city: Yup.string().required("City is required"),
  // state: Yup.string().required("State is required"),
  // zipCode: Yup.string().required("ZIP Code is required"),
  agreeTerms: Yup.boolean()
    .oneOf([true], "You must agree to the terms and conditions")
    .required(),
});

export default function SignUp() {
  const navigate = useNavigate(); // Get navigate function

  const handleRedirect = (url: string) => {
    navigate(`${url}`); // Replace with the URL you want to redirect to
  };
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange", // Enable live validation
  });

  const onSubmit = (data: any) => {
    console.log("Form Data:", data);
    reset(); // Clear form after submission
  };

  return (
    <>
      <div className="flex w-full h-screen  dark:bg-slate-800 bg-white">
        <div className="w-1/2 h-full">
          <img
            loading="lazy"
            src={Register}
            alt="Register"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="w-1/2 h-full flex items-left justify-left ">
          <form onSubmit={handleSubmit(onSubmit)} className="p-7">
            <div className="w-full flex flex-col ">
              <div className="flex">
                <div className="  transition transform ease-in-out duration-500 flex border-4 dark:border-white border-[#0F172A] bg-[#1E293B] dark:hover:bg-blue-500 hover:bg-purple-500 p-3 rounded-full text-white hover:rotate-45">
                  <a href="/admin/dashboard">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                      stroke="red"
                      className="w-10 h-10"
                    >
                      <path d="M18.302 0H22v.003L10.674 24H7.662L2 12h3.727l3.449 7.337z" />
                    </svg>
                  </a>
                </div>
                <div className="mb-1 ml-2 pt-5 text-2xl font-bold text-gray-900  dark:text-white">
                  <h1>VZText</h1>
                </div>
              </div>
              <h2 className="text-2l/7 font-bold text-gray-900  dark:text-white sm:truncate sm:text-3xl sm:tracking-tight pt-3 pb-3 ">
                Create Account
              </h2>
              <span className="text-gray-400 pb-4">
                Enterprise accounts are designed to leverage APIs for
                integration, while Regular accounts can be used to access our
                product.
              </span>
            </div>
            <div className="flex gap-x-6 mb-6">
              <div className="w-full relative">
                <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                  First Name <span className="text-red-500 ml-2">*</span>
                </label>
                <div className="relative ">
                  <Controller
                    name="firstName"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <input
                        type="text"
                        {...field}
                        className={`block w-full h-11 px-5 py-2.5 border rounded-full ${
                          errors.firstName
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter your first name"
                      />
                    )}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs ">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="w-full relative">
                <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                  Last Name<span className="text-red-500 ml-2">*</span>
                </label>
                <Controller
                  name="lastName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      {...field}
                      className={`block w-full h-11 px-5 py-2.5 border rounded-full ${
                        errors.lastName ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your last name"
                    />
                  )}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs ">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-x-6 mb-6">
              <div className="w-full relative">
                <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                  Email Address<span className="text-red-500 ml-2">*</span>
                </label>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="email"
                      {...field}
                      className={`block w-full h-11 px-5 py-2.5 border rounded-full ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your email"
                    />
                  )}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs ">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="w-full relative">
                <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                  Phone Number<span className="text-red-500 ml-2">*</span>
                </label>
                <Controller
                  name="phoneNumber"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      {...field}
                      className={`block w-full h-11 px-5 py-2.5 border rounded-full ${
                        errors.phoneNumber
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter your phone number (digits only)."
                    />
                  )}
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-xs ">
                    {errors.phoneNumber.message}
                  </p>
                )}
              </div>
            </div>

            {/* <div className="relative mb-6">
              <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                Email Address
              </label>
              <Controller
                name="email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <input
                    type="email"
                    {...field}
                    className={`block w-full h-11 px-5 py-2.5 border rounded-full ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter your email"
                  />
                )}
              />
              {errors.email && (
                <p className="text-red-500 text-xs ">
                  {errors.email.message}
                </p>
              )}
            </div> */}
            <div className="flex gap-x-6 mb-6">
              <div className="w-full relative">
                <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                  Password<span className="text-red-500 ml-2">*</span>
                </label>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="password"
                      {...field}
                      className={`block w-full h-11 px-5 py-2.5 border rounded-full ${
                        errors.password ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your password"
                    />
                  )}
                />
                {errors.password && (
                  <p className="text-red-500 text-xs ">
                    {errors.password.message}
                  </p>
                )}
              </div>
              <div className="w-full relative">
                <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                  Confirm Password<span className="text-red-500 ml-2">*</span>
                </label>
                <Controller
                  name="confirmPassword"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="password"
                      {...field}
                      className={`block w-full h-11 px-5 py-2.5 border rounded-full ${
                        errors.confirmPassword
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Confirm your password"
                    />
                  )}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs ">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>
            {/* <div className="relative mb-6">
              <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                Phone Number
              </label>
              <Controller
                name="phoneNumber"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <input
                    type="text"
                    {...field}
                    className={`block w-full h-11 px-5 py-2.5 border rounded-full ${
                      errors.phoneNumber ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter your phone number"
                  />
                )}
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-xs ">
                  {errors.phoneNumber.message}
                </p>
              )}
            </div> */}

            {/* Additional Fields */}
            <div className="flex gap-x-6 mb-6">
              <div className="w-full relative">
                <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                  Company Name<span className="text-red-500 ml-2">*</span>
                </label>
                <Controller
                  name="companyName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      {...field}
                      className={`block w-full h-11 px-5 py-2.5 border rounded-full ${
                        errors.companyName
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter your company name"
                    />
                  )}
                />
                {errors.companyName && (
                  <p className="text-red-500 text-xs ">
                    {errors.companyName.message}
                  </p>
                )}
              </div>
              <div className="w-full relative">
                <label className="flex items-center mb-2 text-gray-600   text-sm font-medium">
                  Website<span className="text-red-500 ml-2">*</span>
                </label>
                <Controller
                  name="website"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      {...field}
                      className={`block w-full h-11 px-5 py-2.5 border ${
                        errors.website ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your website"
                    />
                  )}
                />
                {errors.website && (
                  <p className="text-red-500 text-xs ">
                    {errors.website.message}
                  </p>
                )}
              </div>
              <div className="w-full relative">
                <label className="flex items-center mb-2 text-gray-600 dark:text-white  text-sm font-medium">
                  No. of Employees<span className="text-red-500 ml-2">*</span>
                </label>
                <Controller
                  name="numEmployees"
                  control={control}
                  defaultValue={1}
                  render={({ field }) => (
                    <input
                      type="number"
                      {...field}
                      className={`block w-full h-11  ${
                        errors.numEmployees
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter number of employees"
                    />
                  )}
                />
                {errors.numEmployees && (
                  <p className="text-red-500 text-xs ">
                    {errors.numEmployees.message}
                  </p>
                )}
              </div>
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className="mb-0 flex items-center">
              <div className="relative mb-6">
                <div className="relative mb-6">
                  <Controller
                    name="agreeTerms"
                    control={control}
                    defaultValue={false}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          onChange={(e) => onChange(e.target.checked)} // Convert event to boolean
                          onBlur={onBlur}
                          checked={value} // Use checked for boolean values
                          ref={ref}
                          className={`h-5 w-5 rounded border-gray-300 focus:ring-2 focus:ring-gray-500 ${
                            errors.agreeTerms ? "border-red-500" : ""
                          }`}
                        />
                        <label className="ml-2 text-sm text-gray-600 dark:text-white ">
                          I agree to the Terms and Privacy Policy.
                        </label>
                      </div>
                    )}
                  />
                  {errors.agreeTerms && (
                    <p className="text-red-500 text-xs ">
                      {errors.agreeTerms.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <PillButton
                type="submit"
                variant="dark"
                size="xl"
                className="w-1/2 py-2 px-4 rounded-md transition duration-300"
                disabled={!isValid}
              >
                Sign Up
              </PillButton>
              <PillButton
                onClick={() => {
                  handleRedirect("/home");
                }}
                type="submit"
                variant="primary"
                size="xl"
                className="w-1/2 py-2 px-4 rounded-md transition duration-300"
              >
                Cancel
              </PillButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
